import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import Button from "../../../../../../components/common/PL/Button/Button";
import EmpresaSearch from "./EmpresaSearch";
import RegistrarAutorizacionModal, {
  RegistrarAutorizacionSubmitData,
} from "./RegistrarAutorizacionModal";
import { useStore } from "../../../../../../hooks/useStore";
import { EntranceVehiclePrefill } from "./transportAuthorizationMappers";
import { TransportControlChecks } from "../../../../../../passapp-sdk/types/transportTypes";

import "./SolicitarAutorizacionEgresoModal.css";

type AuthorizingCompany = { id: string; name: string };

export type SolicitarAutorizacionEgresoSubmitData = {
  company: string;
  vehiclePlate: string;
  /** Vacío equivale a "No aplica". */
  trailerPlates: string[];
  trailerNotApplicable: boolean;
  loaded: boolean;
  receipt: string;
  seal: string;
  merchandiseInTransit: boolean;
  controlChecks: TransportControlChecks;
  /** Vacíos si no se marcó el control de custodio vehicular. */
  vehicleCustodyPlate: string;
  vehicleCustodyCompany: string;
  observations: string;
};

type SolicitarAutorizacionEgresoModalProps = {
  open: boolean;
  onClose: () => void;
  /** Empresa que autorizó el ingreso: el egreso se le pide a la misma, así que arranca elegida. */
  prefillCompanyId?: string;
  /** Patentes con las que entró el camión. El acoplado no viene si se quedó adentro. */
  prefillVehicles?: EntranceVehiclePrefill;
  /** Remito ya recuperado en "Observaciones" (el del ingreso): se precarga para no pedirlo dos
   * veces, pero sigue editable por si el camión se retira con otro. */
  prefillRemito?: string;
  /** `approval` presente = el guardia ya consiguió la autorización por otro medio: la solicitud
   * nace aprobada (selfApprove) en lugar de quedar pendiente. */
  onSubmit?: (
    data: SolicitarAutorizacionEgresoSubmitData,
    approval?: RegistrarAutorizacionSubmitData
  ) => Promise<boolean>;
};

const pad = (n: number) => String(n).padStart(2, "0");

const formatDateDisplay = (date: Date) =>
  `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

const sanitizePatente = (value: string) =>
  value
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, 20);

const sanitizeAlfanumerico = (value: string) =>
  value.replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ0-9\- ]/g, "").slice(0, 39);

const SolicitarAutorizacionEgresoModal: React.FC<SolicitarAutorizacionEgresoModalProps> = ({
  open,
  onClose,
  prefillCompanyId,
  prefillVehicles,
  prefillRemito,
  onSubmit,
}) => {
  const { authStore } = useStore();
  const companies: AuthorizingCompany[] = authStore.companies ?? [];

  const [empresaAutorizanteSearchValue, setEmpresaAutorizanteSearchValue] =
    React.useState<string>("");
  const [selectedEmpresaAutorizante, setSelectedEmpresaAutorizante] =
    React.useState<AuthorizingCompany | null>(null);
  const [patenteTractor, setPatenteTractor] = React.useState<string>("");
  const [patentesAcoplado, setPatentesAcoplado] = React.useState<string[]>([""]);
  const [acopladoNoAplica, setAcopladoNoAplica] = React.useState<boolean>(false);
  const [cargado, setCargado] = React.useState<boolean | null>(null);
  const [mercaderiaEnTransito, setMercaderiaEnTransito] = React.useState<boolean>(false);
  const [numeroRemito, setNumeroRemito] = React.useState<string>("");
  const [numeroPrecinto, setNumeroPrecinto] = React.useState<string>("");
  const [controlCustodioVehicular, setControlCustodioVehicular] = React.useState<boolean>(false);
  const [custodioPatente, setCustodioPatente] = React.useState<string>("");
  const [custodioEmpresa, setCustodioEmpresa] = React.useState<string>("");
  const [controlCustodioSatelital, setControlCustodioSatelital] = React.useState<boolean>(false);
  const [controlRemitoManual, setControlRemitoManual] = React.useState<boolean>(false);
  const [controlPrecintoManual, setControlPrecintoManual] = React.useState<boolean>(false);
  const [observaciones, setObservaciones] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isRegistrarModalOpen, setIsRegistrarModalOpen] = React.useState(false);

  // El guardia puede cambiarla: sólo se siembra al abrir, no en cada render.
  React.useEffect(() => {
    if (!open || !prefillCompanyId) return;
    const empresa = companies.find((c) => String(c.id) === String(prefillCompanyId));
    if (!empresa) return;
    setSelectedEmpresaAutorizante(empresa);
    setEmpresaAutorizanteSearchValue(empresa.name);
  }, [open, prefillCompanyId, companies]);

  // Mismo criterio que la empresa: se siembra al abrir y el guardia puede corregir las patentes,
  // p. ej. si el camión sale con otro acoplado.
  React.useEffect(() => {
    if (!open || !prefillVehicles) return;

    const { vehiclePlate, trailerPlates, trailerNotApplicable } = prefillVehicles;
    if (vehiclePlate) setPatenteTractor(sanitizePatente(vehiclePlate));

    if (trailerPlates.length > 0) {
      setPatentesAcoplado(trailerPlates.map(sanitizePatente));
      setAcopladoNoAplica(false);
    } else if (trailerNotApplicable) {
      // Los acoplados quedaron adentro (desenganche) o el ingreso ya decía "no aplica".
      setPatentesAcoplado([""]);
      setAcopladoNoAplica(true);
    }
  }, [open, prefillVehicles]);

  // El remito de Observaciones se precarga al abrir (queda visible al elegir "Sí, cargado").
  React.useEffect(() => {
    if (!open) return;
    setNumeroRemito(sanitizeAlfanumerico(prefillRemito ?? ""));
  }, [open, prefillRemito]);

  const updatePatenteAcoplado =(index: number, value: string) =>
    setPatentesAcoplado((prev) =>
      prev.map((patente, i) => (i === index ? sanitizePatente(value) : patente))
    );

  const addPatenteAcoplado = () => setPatentesAcoplado((prev) => [...prev, ""]);

  const removePatenteAcoplado = (index: number) =>
    setPatentesAcoplado((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const resetState = () => {
    setEmpresaAutorizanteSearchValue("");
    setSelectedEmpresaAutorizante(null);
    setPatenteTractor("");
    setPatentesAcoplado([""]);
    setAcopladoNoAplica(false);
    setCargado(null);
    setMercaderiaEnTransito(false);
    setNumeroRemito("");
    setNumeroPrecinto("");
    setControlCustodioVehicular(false);
    setCustodioPatente("");
    setCustodioEmpresa("");
    setControlCustodioSatelital(false);
    setControlRemitoManual(false);
    setControlPrecintoManual(false);
    setObservaciones("");
  };

  // Remito/precinto controls are auto-checked and locked while their numbers are set
  const remitoFilled = cargado === true && numeroRemito.trim().length > 0;
  const precintoFilled = cargado === true && numeroPrecinto.trim().length > 0;
  const controlRemito = remitoFilled || controlRemitoManual;
  const controlPrecinto = precintoFilled || controlPrecintoManual;

  const patentesAcopladoLlenas = patentesAcoplado.filter((patente) => patente.length > 0);
  const isValid =
    !!selectedEmpresaAutorizante &&
    patenteTractor.length > 0 &&
    (acopladoNoAplica || patentesAcopladoLlenas.length > 0) &&
    cargado !== null &&
    (cargado === false || numeroRemito.trim().length > 0) &&
    (!controlCustodioVehicular || custodioPatente.length > 0);

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const buildSubmitData = (empresa: AuthorizingCompany): SolicitarAutorizacionEgresoSubmitData => ({
    company: empresa.id,
    vehiclePlate: patenteTractor,
    trailerPlates: acopladoNoAplica ? [] : patentesAcopladoLlenas,
    trailerNotApplicable: acopladoNoAplica,
    loaded: cargado === true,
    receipt: cargado === true ? numeroRemito.trim() : "",
    seal: cargado === true ? numeroPrecinto.trim() : "",
    merchandiseInTransit: cargado === false && mercaderiaEnTransito,
    controlChecks: {
      receipt: controlRemito,
      seal: controlPrecinto,
      vehicleCustody: controlCustodioVehicular,
      satelliteCustody: controlCustodioSatelital,
    },
    vehicleCustodyPlate: controlCustodioVehicular ? custodioPatente : "",
    vehicleCustodyCompany: controlCustodioVehicular ? custodioEmpresa.trim() : "",
    observations: observaciones.trim(),
  });

  const handleSubmit = async () => {
    if (!isValid || isSubmitting || !selectedEmpresaAutorizante) return;
    setIsSubmitting(true);
    const success = (await onSubmit?.(buildSubmitData(selectedEmpresaAutorizante))) ?? true;
    setIsSubmitting(false);
    if (success) {
      resetState();
      onClose();
    }
  };

  // El guardia ya tiene la autorización de palabra: se manda la solicitud y la aprobación juntas.
  const handleRegistrarPorOtroMedio = async (
    approval: RegistrarAutorizacionSubmitData
  ): Promise<boolean> => {
    if (!isValid || !selectedEmpresaAutorizante) return false;
    const success =
      (await onSubmit?.(buildSubmitData(selectedEmpresaAutorizante), approval)) ?? true;
    if (success) {
      resetState();
      setIsRegistrarModalOpen(false);
      onClose();
    }
    return success;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ className: "solicitarEgresoModalCard" }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle disableTypography className="solicitarEgresoModalTitle">
        Solicitar autorización de egreso
      </DialogTitle>
      <DialogContent className="solicitarEgresoModalContent">
        <div className="solicitarEgresoModalSection">
          <span className="solicitarEgresoModalSectionLabel">Empresa autorizante *</span>
          <EmpresaSearch
            searchValue={empresaAutorizanteSearchValue}
            onSearchChange={setEmpresaAutorizanteSearchValue}
            selectedEmpresa={selectedEmpresaAutorizante}
            onSelect={setSelectedEmpresaAutorizante}
            onClear={() => {
              setSelectedEmpresaAutorizante(null);
              setEmpresaAutorizanteSearchValue("");
            }}
            options={companies}
            getLabel={(empresa) => empresa.name}
            placeholder="-completar-"
            disabled={isSubmitting}
          />
        </div>

        <div className="solicitarEgresoModalSection">
          <span className="solicitarEgresoModalSectionLabel">Fecha de egreso</span>
          <div className="solicitarEgresoModalReadOnlyValue">{formatDateDisplay(new Date())}</div>
        </div>

        <div className="solicitarEgresoModalSection">
          <div className="solicitarEgresoModalFieldRow">
            <div className="solicitarEgresoModalField">
              <span className="solicitarEgresoModalSectionLabel">Patente vehículo (tractor) *</span>
              <input
                type="text"
                className="solicitarEgresoModalTextInput"
                value={patenteTractor}
                onChange={(e) => setPatenteTractor(sanitizePatente(e.target.value))}
                placeholder="-completar-"
                disabled={isSubmitting}
              />
            </div>
            <div className="solicitarEgresoModalField">
              <span className="solicitarEgresoModalSectionLabel">Patente acoplado *</span>
              {acopladoNoAplica ? (
                <input
                  type="text"
                  className="solicitarEgresoModalTextInput"
                  value=""
                  placeholder="-completar-"
                  disabled
                />
              ) : (
                <>
                  {patentesAcoplado.map((patente, index) => (
                    <div className="solicitarEgresoModalAcopladoRow" key={index}>
                      <input
                        type="text"
                        className="solicitarEgresoModalTextInput"
                        value={patente}
                        onChange={(e) => updatePatenteAcoplado(index, e.target.value)}
                        placeholder="-completar-"
                        disabled={isSubmitting}
                      />
                      {patentesAcoplado.length > 1 && (
                        <button
                          type="button"
                          className="solicitarEgresoModalAcopladoRemove"
                          onClick={() => removePatenteAcoplado(index)}
                          disabled={isSubmitting}
                          aria-label="Quitar acoplado"
                        >
                          <CloseIcon fontSize="small" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="solicitarEgresoModalAddAcoplado"
                    onClick={addPatenteAcoplado}
                    disabled={isSubmitting}
                  >
                    + Agregar acoplado
                  </button>
                </>
              )}
              <FormControlLabel
                className="solicitarEgresoModalCheckbox"
                control={
                  <Checkbox
                    checked={acopladoNoAplica}
                    onChange={(e) => {
                      setAcopladoNoAplica(e.target.checked);
                      if (e.target.checked) setPatentesAcoplado([""]);
                    }}
                    disabled={isSubmitting}
                    size="small"
                  />
                }
                label="No aplica"
              />
            </div>
          </div>
        </div>

        <div className="solicitarEgresoModalSection">
          <span className="solicitarEgresoModalSectionLabel">
            ¿Se retira con carga de su empresa? *
          </span>
          <div className="solicitarEgresoModalToggleRow">
            <button
              type="button"
              className={`solicitarEgresoModalToggleButton ${
                cargado === true ? "solicitarEgresoModalToggleButtonSelected" : ""
              }`}
              onClick={() => setCargado(true)}
              disabled={isSubmitting}
            >
              Sí, cargado
            </button>
            <button
              type="button"
              className={`solicitarEgresoModalToggleButton ${
                cargado === false ? "solicitarEgresoModalToggleButtonSelected" : ""
              }`}
              onClick={() => setCargado(false)}
              disabled={isSubmitting}
            >
              No, vacío
            </button>
          </div>
          {cargado === false && (
            <FormControlLabel
              className="solicitarEgresoModalCheckbox"
              control={
                <Checkbox
                  checked={mercaderiaEnTransito}
                  onChange={(e) => setMercaderiaEnTransito(e.target.checked)}
                  disabled={isSubmitting}
                  size="small"
                />
              }
              label="Sale con mercadería en tránsito"
            />
          )}
        </div>

        {cargado === true && (
          <div className="solicitarEgresoModalSection">
            <div className="solicitarEgresoModalFieldRow">
              <div className="solicitarEgresoModalField">
                <span className="solicitarEgresoModalSectionLabel">N° de remito *</span>
                <input
                  type="text"
                  className="solicitarEgresoModalTextInput"
                  value={numeroRemito}
                  onChange={(e) => setNumeroRemito(sanitizeAlfanumerico(e.target.value))}
                  placeholder="-completar-"
                  disabled={isSubmitting}
                />
              </div>
              <div className="solicitarEgresoModalField">
                <span className="solicitarEgresoModalSectionLabel">N° de precinto</span>
                <input
                  type="text"
                  className="solicitarEgresoModalTextInput"
                  value={numeroPrecinto}
                  onChange={(e) => setNumeroPrecinto(sanitizeAlfanumerico(e.target.value))}
                  placeholder="-completar-"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        )}

        <div className="solicitarEgresoModalSection">
          <span className="solicitarEgresoModalSectionLabel">Controles de egreso</span>
          <div className="solicitarEgresoModalControlesGrid">
            <FormControlLabel
              className="solicitarEgresoModalCheckbox"
              control={
                <Checkbox
                  checked={controlRemito}
                  onChange={(e) => setControlRemitoManual(e.target.checked)}
                  disabled={remitoFilled || isSubmitting}
                  size="small"
                />
              }
              label="Remito"
            />
            <FormControlLabel
              className="solicitarEgresoModalCheckbox"
              control={
                <Checkbox
                  checked={controlPrecinto}
                  onChange={(e) => setControlPrecintoManual(e.target.checked)}
                  disabled={precintoFilled || isSubmitting}
                  size="small"
                />
              }
              label="Precinto"
            />
            <FormControlLabel
              className="solicitarEgresoModalCheckbox"
              control={
                <Checkbox
                  checked={controlCustodioVehicular}
                  onChange={(e) => {
                    setControlCustodioVehicular(e.target.checked);
                    if (!e.target.checked) {
                      setCustodioPatente("");
                      setCustodioEmpresa("");
                    }
                  }}
                  disabled={isSubmitting}
                  size="small"
                />
              }
              label="Custodio vehicular"
            />
            <FormControlLabel
              className="solicitarEgresoModalCheckbox"
              control={
                <Checkbox
                  checked={controlCustodioSatelital}
                  onChange={(e) => setControlCustodioSatelital(e.target.checked)}
                  disabled={isSubmitting}
                  size="small"
                />
              }
              label="Custodio satelital"
            />
          </div>
          {controlCustodioVehicular && (
            <div className="solicitarEgresoModalFieldRow solicitarEgresoModalCustodioFields">
              <div className="solicitarEgresoModalField">
                <span className="solicitarEgresoModalSectionLabel">Patente custodio *</span>
                <input
                  type="text"
                  className="solicitarEgresoModalTextInput"
                  value={custodioPatente}
                  onChange={(e) => setCustodioPatente(sanitizePatente(e.target.value))}
                  placeholder="-completar-"
                  disabled={isSubmitting}
                />
              </div>
              <div className="solicitarEgresoModalField">
                <span className="solicitarEgresoModalSectionLabel">Empresa custodio</span>
                <input
                  type="text"
                  className="solicitarEgresoModalTextInput"
                  value={custodioEmpresa}
                  onChange={(e) => setCustodioEmpresa(sanitizeAlfanumerico(e.target.value))}
                  placeholder="-completar-"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          )}
        </div>

        <div className="solicitarEgresoModalSection">
          <span className="solicitarEgresoModalSectionLabel">
            Observaciones para el autorizante
          </span>
          <textarea
            className="solicitarEgresoModalTextArea"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value.slice(0, 1000))}
            placeholder="-completar-"
            rows={3}
            disabled={isSubmitting}
          />
          <span className="solicitarEgresoModalCounter">{observaciones.length}/1000</span>
        </div>
      </DialogContent>
      <DialogActions className="solicitarEgresoModalActions">
        <Button
          variant="outline"
          onClick={handleClose}
          className="solicitarEgresoModalButton"
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          variant="neutral"
          onClick={handleSubmit}
          className="solicitarEgresoModalButton"
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
        >
          Solicitar autorización
        </Button>
      </DialogActions>

      <div className="solicitarEgresoModalSecondaryActions">
        <Button
          variant="outline"
          onClick={() => setIsRegistrarModalOpen(true)}
          className="solicitarEgresoModalButton transportRegistrarOtroMedioButton"
          disabled={!isValid || isSubmitting}
        >
          Registrar autorización por otro medio
        </Button>
      </div>

      <RegistrarAutorizacionModal
        open={isRegistrarModalOpen}
        onClose={() => setIsRegistrarModalOpen(false)}
        variant="salida"
        prefillObservations={observaciones}
        onSubmit={handleRegistrarPorOtroMedio}
      />
    </Dialog>
  );
};

export default SolicitarAutorizacionEgresoModal;
